/**
 * Sentry error reporting, cross-platform.
 *
 * Web: `@sentry/react` initialised with the standard browser integrations.
 * Native: `@sentry/capacitor` lazy-imported so the web bundle does not
 * pull in the Capacitor bridge. On native we still wire `@sentry/react`
 * underneath because it surfaces React component stacks.
 *
 * Reads the DSN from `VITE_SENTRY_DSN`. If the DSN is missing, the call
 * is a no-op — staging and local dev run without a Sentry project.
 */

import * as SentryReact from '@sentry/react'
import { isNative } from './index'
import { hasConsent } from '@/lib/consent'

let initialized = false

export async function initializeTelemetry(): Promise<void> {
  if (initialized) return
  // CH/EU compliance: don't initialise Sentry until the user has explicitly
  // opted in via the cookie consent banner. Call sites should re-invoke
  // initializeTelemetry() after consent flips to 'granted'.
  if (!hasConsent()) return
  const dsn = import.meta.env.VITE_SENTRY_DSN as string | undefined
  if (!dsn) return
  initialized = true

  const release = (import.meta.env.VITE_RELEASE as string | undefined) ?? 'circleup-mobile@dev'
  const environment = (import.meta.env.MODE as string | undefined) ?? 'production'

  if (isNative()) {
    try {
      const capacitorSentry = await import(/* @vite-ignore */ '@sentry/capacitor')
      capacitorSentry.init(
        {
          dsn,
          release,
          environment,
          tracesSampleRate: 0.1,
          // Don't pick up DOM breadcrumbs that would leak into Sentry UI
          // before users have agreed to analytics.
          sendDefaultPii: false,
        },
        SentryReact.init,
      )
      return
    } catch {
      /* plugin missing — fall back to the web SDK alone */
    }
  }

  SentryReact.init({
    dsn,
    release,
    environment,
    tracesSampleRate: 0.1,
    sendDefaultPii: false,
  })
}

export function captureException(error: unknown, context?: Record<string, unknown>): void {
  if (!initialized) return
  SentryReact.captureException(error, context ? { extra: context } : undefined)
}

export function setUser(user: { id: string; email?: string } | null): void {
  if (!initialized) return
  SentryReact.setUser(user)
}

/**
 * Record a non-essential product-analytics event (funnel steps, segment
 * signals). CH/EU compliance: no-op unless the user has granted consent —
 * never send PII in `props`. Lands as a Sentry breadcrumb (visible in error
 * context) and, if a tag manager is ever mounted, on `window.dataLayer`.
 */
export function trackEvent(name: string, props: Record<string, string | number | boolean> = {}): void {
  if (!hasConsent()) return
  try {
    SentryReact.addBreadcrumb({ category: 'analytics', type: 'info', level: 'info', message: name, data: props })
  } catch {
    /* Sentry not initialised — breadcrumb is best-effort */
  }
  const w = window as unknown as { dataLayer?: Array<Record<string, unknown>> }
  if (Array.isArray(w.dataLayer)) w.dataLayer.push({ event: name, ...props })
}
